import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, } from 'react';
import { doorLabels, slideButtons, slideId, stepLayout, } from './door-window.js';
import { RichText } from './RichText.js';
function targetOf(context, element, labelKey) {
    return {
        step: context.step,
        element,
        ...(context.slide === undefined ? {} : { slide: context.slide }),
        ...(labelKey === undefined ? {} : { labelKey }),
    };
}
/** An element the configurator can choose: its class, its click and focus, its settings. */
function choosable(context, element, className, render, labelKey) {
    const item = context.edit?.item?.(targetOf(context, element, labelKey));
    const classes = item?.className ? `${className} ${item.className}` : className;
    const select = item?.onSelect;
    const shown = render({
        className: classes,
        ...(select === undefined ? {} : { onClick: select, onFocus: select }),
        ...(item?.onDragOver === undefined ? {} : { onDragOver: item.onDragOver }),
        ...(item?.onDragLeave === undefined ? {} : { onDragLeave: item.onDragLeave }),
        ...(item?.onDrop === undefined ? {} : { onDrop: item.onDrop }),
        // Enter on the element itself; on a grip inside it Enter stays the grip's.
        ...(item?.focusable === true && select !== undefined
            ? {
                tabIndex: 0,
                onKeyDown: (event) => {
                    if (event.key === 'Enter' && event.target === event.currentTarget) {
                        event.preventDefault();
                        select();
                    }
                },
            }
            : {}),
    });
    /*
      The settings stand right after the element in a box of their own: door.css places the
      children of a step, beside a picture in two columns, and gives the box a row across both.
    */
    return item?.controls ? (_jsxs(_Fragment, { children: [shown, _jsx("div", { className: "countdown-door-window__controls", children: item.controls })] })) : (shown);
}
/** A text the configurator edits in place, or the text itself. */
function editable(context, element, shown, labelKey) {
    const text = context.edit?.text;
    return text === undefined ? shown : text(targetOf(context, element, labelKey), shown);
}
/** An element's content with the host's grip after it; without a grip the content alone. */
function withGrip(content, grip) {
    return grip === undefined || grip === null ? (content) : (_jsxs(_Fragment, { children: [content, grip] }));
}
function Media({ image, video, ratio, }) {
    /*
      A video (package H) plays only on request: `preload="none"` fetches nothing but the poster
      until somebody presses play, so the door at midnight weighs what a picture weighs, and no
      autoplay. `playsinline` keeps iOS from taking the page to full screen. It keeps its own
      format: a fixed ratio would crop what the sponsor filmed.
    */
    if (video !== null) {
        return (_jsxs(_Fragment, { children: [_jsxs("video", { className: "countdown-content-video", controls: true, preload: "none", playsInline: true, poster: video.poster, "aria-label": video.label, children: [_jsx("source", { src: video.src, type: "video/mp4" }), video.captions !== null ? (_jsx("track", { kind: "captions", src: video.captions, srcLang: "de", label: "Deutsch", default: true })) : null, "Dieses Video l\u00E4sst sich hier nicht abspielen. Die Textfassung steht darunter."] }), _jsxs("details", { className: "countdown-video-text", children: [_jsx("summary", { children: "Textfassung des Videos" }), _jsx("p", { children: video.text })] })] }));
    }
    if (image === null) {
        return null;
    }
    /*
      Width and height are absent on purpose: they are unknown here, and a guessed pair would
      distort the picture. The format comes from the layout (`data-ratio`), `auto` keeps the
      picture's own. `alt` is never empty: the API drops a picture without its text.
    */
    return (_jsx("img", { className: "countdown-door-step__picture", src: image.src, alt: image.alt, "data-ratio": ratio, loading: "lazy", decoding: "async" }));
}
/** The elements of one step in the layout's order. */
function Step({ content, context, layout, presentation, }) {
    const hasMedia = content.image !== null || content.video !== null;
    const hasText = content.blocks.length > 0 || content.paragraphs.length > 0;
    /*
      In the dialog a picture across the window and first in the order runs to the window's
      edges, as in the popup of 2025.
    */
    const bleeds = presentation === 'dialog' && hasMedia && layout.image_side === 'full' && layout.order[0] === 'image';
    const present = {
        image: hasMedia,
        title: content.title !== null && content.title !== '',
        text: hasText,
    };
    const shown = layout.order.filter((element) => present[element]);
    const move = context.edit?.move;
    const grip = (element) => move === undefined ? undefined : move(targetOf(context, element), shown);
    const elements = layout.order.map((element) => {
        switch (element) {
            case 'image':
                return hasMedia
                    ? choosable(context, 'image', 'countdown-door-step__image', (props) => (_jsx("div", { ...props, children: withGrip(_jsx(Media, { image: content.image, video: content.video, ratio: layout.ratio }), grip('image')) })))
                    : null;
            case 'title':
                return content.title !== null && content.title !== ''
                    ? choosable(context, 'title', 'countdown-door-step__title', (props) => (_jsx("h2", { ...props, id: content.titleId, tabIndex: content.titleId === undefined ? props.tabIndex : -1, children: withGrip(editable(context, 'title', content.title), grip('title')) })))
                    : null;
            case 'text':
                return hasText
                    ? choosable(context, 'text', 'countdown-door-step__text', (props) => (_jsx("div", { ...props, children: withGrip(editable(context, 'text', _jsxs(_Fragment, { children: [_jsx(RichText, { blocks: content.blocks, sponsored: context.sponsored, still: context.edit !== undefined }), content.paragraphs.map((paragraph, index) => (_jsx("p", { children: paragraph }, index)))] })), grip('text')) })))
                    : null;
        }
    });
    return (_jsx("div", { className: "countdown-door-step", "data-image-side": hasMedia ? layout.image_side : undefined, "data-bleed": bleeds ? '' : undefined, children: elements.map((element, index) => (_jsx(Fragment, { children: element }, layout.order[index]))) }));
}
/**
 * A button of the window: a link dressed as one. A span where it leads nowhere: in the
 * configurator, and for the quiz's submit button without the page's form.
 */
function Button({ button, context, quiet = false, labelKey, }) {
    const className = quiet
        ? 'countdown-button countdown-button--quiet countdown-door-window__button'
        : 'countdown-button countdown-door-window__button';
    const label = editable(context, 'button', button.label, labelKey);
    return choosable(context, 'button', className, (props) => context.edit !== undefined || button.href === null ? (_jsx("span", { ...props, children: label })) : 'external' in button && button.external === true ? (
    /*
      Always a new tab (24.10.2026, IT lead): the participant keeps the calendar open and
      comes back to the quiz.
    */
    _jsxs("a", { ...props, href: button.href, rel: context.sponsored ? 'sponsored noopener' : 'noopener', target: "_blank", children: [label, _jsx("span", { className: "countdown-sr-only", children: " (externer Link)" })] })) : (_jsx("a", { ...props, href: button.href, children: label })), labelKey);
}
/**
 * A slot of the page. Astro hands a slot in wrapped in an element of its own, so the slot gets a
 * box of the window's rather than taking part in the spacing rules of its neighbours.
 */
function Slot({ className, children, }) {
    return children === undefined || children === null || children === false ? null : (_jsx("div", { className: className, children: children }));
}
function Slides({ props, context, presentation, }) {
    const slides = props.slides ?? [];
    const buttons = slideButtons(slides, {
        quizHref: props.quizHref ?? null,
        toQuizLabel: doorLabels(props.layout).to_quiz,
    });
    const shape = stepLayout(props.layout, 'slide');
    return (_jsx("div", { className: "countdown-door-window__slides", children: slides.map((slide, index) => {
            if (props.activeSlide !== undefined && props.activeSlide !== index) {
                return null;
            }
            const slideContext = { ...context, slide: index };
            const { primary, link: drawn } = buttons[index] ?? { primary: null, link: null };
            // The configurator types a link's text on it, also before its address is an https one.
            const own = slide.link ?? (slide.button?.action === 'link' ? slide.button : null);
            const link = drawn ?? (context.edit !== undefined && own != null ? { label: own.label, href: null } : null);
            const last = index + 1 === slides.length;
            const addLink = link === null ? (context.edit?.addLink?.(index) ?? null) : null;
            return (_jsxs("section", { id: slideId(index), className: "countdown-door-window__slide", "aria-label": slides.length > 1 ? `Folie ${index + 1} von ${slides.length}` : undefined, children: [_jsx(Step, { content: {
                            image: slide.image,
                            video: slide.video ?? null,
                            title: slide.title,
                            blocks: slide.blocks,
                            paragraphs: [],
                        }, context: slideContext, layout: shape, presentation: presentation }), link !== null || primary !== null || addLink !== null ? (_jsxs("div", { className: "countdown-door-window__actions", children: [addLink, link !== null ? (_jsx(Button, { button: link, context: slideContext, quiet: true, labelKey: "link" })) : null, primary !== null ? (_jsx(Button, { button: primary, context: slideContext, labelKey: last ? 'to_quiz' : 'next' })) : null] })) : null] }, index));
        }) }));
}
export function DoorWindow(props) {
    const { step, layout } = props;
    const presentation = props.presentation ?? 'page';
    const context = {
        step,
        sponsored: props.sponsored,
        edit: props.edit,
    };
    const panel = props.panel ?? {};
    const action = props.action ?? null;
    return (_jsxs("section", { id: props.id, className: "countdown-door-window", "data-step": step, "data-presentation": presentation, children: [step === 'slide' ? (_jsx(Slides, { props: props, context: context, presentation: presentation })) : (_jsx(Step, { content: {
                    image: panel.image ?? null,
                    video: null,
                    title: panel.title ?? null,
                    titleId: panel.titleId,
                    blocks: panel.blocks ?? [],
                    paragraphs: panel.paragraphs ?? [],
                }, context: context, layout: stepLayout(layout, step), presentation: presentation })), _jsx(Slot, { className: "countdown-door-window__notice", children: props.notice }), _jsx(Slot, { className: "countdown-door-window__form", children: props.form }), step === 'quiz' && props.form === undefined ? (_jsx("div", { className: "countdown-door-window__actions", children: _jsx(Button, { button: { label: doorLabels(layout).submit, href: null }, context: context, labelKey: "submit" }) })) : null, action !== null ? (_jsx("div", { className: "countdown-door-window__actions", children: _jsx(Button, { button: action, context: context, quiet: action.quiet ?? false, labelKey: action.labelKey }) })) : null, _jsx(Slot, { className: "countdown-door-window__footer", children: props.footer })] }));
}
//# sourceMappingURL=DoorWindow.js.map